// apps/client/src/ui/chat/Bubbles.tsx (M0-T19) — chat bubbles and emote badges above residents' name tags (same space only; the net
// handler decides). mountBubbles() adds its own React root inside #ui once, so the HUD needs no edits; each resident's row (the emote
// badge beside the chat bubble, one bubble high so a speaker near the top of the screen is not pushed down under it) is portalled into
// tagAnchor(r), where the name tags' overlap pass measures it.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { tagAnchor } from '../../characters/nametags';
import { getGame } from '../../game/instance';
import { safetyBlockedNow } from '../../state/slices/safety';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Bubble } from '../kit';
import { GLYPHS } from './emoteGlyphs';

const CSS = `
.tag-bubble { position: absolute; bottom: 120%; left: 50%; transform: translateX(-50%); width: max-content; max-width: 260px;
  pointer-events: none; display: flex; align-items: flex-end; gap: 6px; animation: tag-bubble-in .22s var(--ease-out, ease-out); }
.tag-bubble .bubble { overflow-wrap: anywhere; }
.emote-badge { width: 44px; height: 44px; border-radius: 50%; background: #fff; box-shadow: var(--sh-soft); display: grid; place-items: center;
  color: var(--wood-800); animation: emote-pop .36s cubic-bezier(.3,1.6,.5,1); }
.emote-badge svg { width: 28px; height: 28px; }
.emote-badge[data-emote='heart'] { color: var(--flag-500); }
.emote-badge[data-emote='heart'] svg path { fill: var(--coral-400); }
@keyframes tag-bubble-in { from { opacity: 0; transform: translate(-50%, 6px) scale(.92); } to { opacity: 1; transform: translateX(-50%); } }
@keyframes emote-pop { from { transform: scale(.4); } to { transform: scale(1); } }
@media (prefers-reduced-motion: reduce) { .tag-bubble, .emote-badge { animation: none; } }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'chat-bubbles';
  el.textContent = CSS;
  document.head.appendChild(el);
}

export function BubblesLayer() {
  const t = useT();
  const bubbles = useGame((s) => s.chatBubbles);
  const badges = useGame((s) => s.emoteBadges);
  useEffect(() => {
    const id = setInterval(() => {
      const s = useGame.getState();
      // bubbles and badges exist only in town, where the game does
      if (Object.keys(s.chatBubbles).length || Object.keys(s.emoteBadges).length) s.chatExpire(getGame().now());
    }, 250);
    return () => clearInterval(id);
  }, []);
  const residents = useGame((s) => s.residents);
  const blocked = useGame(safetyBlockedNow);
  // M2-T08: no bubble for a blocked player's account, even one already showing when you blocked them
  const who = ([...new Set([...Object.keys(badges), ...Object.keys(bubbles)])] as (keyof typeof bubbles)[])
    .filter((r) => !(residents[r]?.userId && residents[r].userId in blocked));
  return <>{who.map((r) => {
    const a = tagAnchor(r), b = bubbles[r], e = badges[r];
    return a ? createPortal(
      <div className="tag-bubble" data-tag-key={e?.emote ?? ''}>
        {e && <div key={e.until} className="emote-badge" data-emote={e.emote} role="img" aria-label={t(`emotes.${e.emote}`)}>{GLYPHS[e.emote]}</div>}
        {b && <Bubble>{b.text}</Bubble>}
      </div>, a, r) : null;
  })}</>;
}

let mounted = false;
export function mountBubbles(): void {
  if (mounted || typeof document === 'undefined') return;
  const ui = document.getElementById('ui');
  if (!ui) return;
  mounted = true;
  injectStyle();
  const host = document.createElement('div');
  host.dataset.owner = 'chat-bubbles';
  ui.append(host);
  createRoot(host).render(<BubblesLayer />);
}
